import { memo, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Icon } from '~/components';
import { Button } from '~/components/Button';
import { showToast } from '~/components/Toast';
import { cn } from '~/lib/cn';
import { ERROR_FORM_LINK } from '~/modules/artist-roster/constants';
import { useIsContactInfoEnabled } from '~/modules/artist-roster/hooks/useIsContactInfoEnabled/useIsContactInfoEnabled';
import { exportArtistsCSV } from '~/modules/artist-roster/lib/artists';
import { getRosterStatus } from '~/modules/artist-roster/lib/roster';
import type { LabelData } from '~/modules/artist-roster/types';
import { useUser } from '~/modules/auth/hooks/useUser';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

import { ArtistExportDialog } from './components/ArtistExportDialog';
import { useHasExportContactInfoPermission } from './utils/hasExportContactInfoPermission';

const TOAST_CLASS = cn('min-w-[275px] pb-[0.75rem]');

interface ArtistExportProps {
  search?: string;
  filteredLabels?: number[];
  isCrossover?: boolean;
  isOnRoster?: boolean;
  className?: string;
  allLabels?: LabelData[];
}

/**
 * This component is wrapped in React.memo() to prevent unnecessary re-renders with
 * the ScrollCollapse logic. See https://github.com/wmgtech/wmg2-one-app/pull/1736
 */
export const ArtistExport: React.FC<ArtistExportProps> = memo(
  ({
    search = '',
    className = '',
    isCrossover = false,
    isOnRoster = undefined,
    filteredLabels = [],
    allLabels,
  }: ArtistExportProps) => {
    const { t } = useTranslation('roster');
    const [isExporting, setIsExporting] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const { trackEvent } = useAnalytics('Artist Roster');
    const { enabled: isContactDetailsEnabled } = useIsContactInfoEnabled();

    const { permissions } = useUser();

    const hasExportContactInfoPermission = useHasExportContactInfoPermission(
      permissions,
      filteredLabels,
      allLabels,
    );

    const abortControllerRef = useRef<AbortController | null>(null);

    const exportCSV = async (withContactInfo: boolean) => {
      setIsExporting(true);
      setIsDialogOpen(false);

      abortControllerRef.current = new AbortController();

      try {
        toast.dismiss();
        showToast({
          type: 'loading',
          title: t('export.loading.title'),
          description: t('export.loading.description'),
          duration: Infinity,
          className: TOAST_CLASS,
        });

        const isExportingComplete = await exportArtistsCSV({
          search,
          labels: filteredLabels,
          isCrossover,
          isOnRoster,
          ...(withContactInfo ? { withContactInfo } : {}),
          signal: abortControllerRef.current.signal,
        });

        if (isExportingComplete) {
          toast.dismiss();
          showToast({
            type: 'success',
            title: t('export.success.title'),
            description: t('export.success.description'),
            className: TOAST_CLASS,
          });

          trackEvent('rosterExported', {
            roster_label_filters_count: filteredLabels.length,
            roster_label_filters_applied: filteredLabels.length > 0,
            roster_crossover_filter_applied: isCrossover,
            roster_status: getRosterStatus(isOnRoster),
            contact_info_exported:
              isContactDetailsEnabled && hasExportContactInfoPermission,
          });
        }
      } catch {
        toast.dismiss();
        showToast({
          type: 'error',
          title: t('export.error.title'),
          description: (
            <Trans
              t={t}
              i18nKey="export.error.description"
              components={{
                a: (
                  <a
                    href={ERROR_FORM_LINK}
                    className="underline"
                    target="_blank"
                    aria-label={t('export.error.aria')}
                    rel="noreferrer"
                  />
                ),
              }}
            />
          ),
          duration: Infinity,
          className: TOAST_CLASS,
        });
      } finally {
        setIsExporting(false);
      }
    };

    const handleExportButtonClick = async () => {
      if (isContactDetailsEnabled && hasExportContactInfoPermission) {
        setIsDialogOpen(true);

        return;
      }

      await exportCSV(false);
    };

    const handleDialogChange = (state: boolean) => {
      setIsDialogOpen(state);
    };

    useEffect(() => {
      return () => {
        // Cancel the network request to export the CSV if the component unmounts.
        abortControllerRef.current?.abort();
        toast.dismiss();
      };
    }, []);

    return (
      <>
        <div>
          <Button
            variant="text"
            className={cn(
              'flex items-center justify-center gap-sm font-semibold',
              className,
            )}
            onClick={handleExportButtonClick}
            disabled={isExporting}
          >
            {isExporting
              ? t('export.labels.exporting')
              : t('export.labels.export')}
            <div className="inline-block">
              <Icon type="file-export" variant="regular" size="sm" />
            </div>
          </Button>
        </div>
        <ArtistExportDialog
          isOpen={isDialogOpen}
          onExportClick={exportCSV}
          onChange={handleDialogChange}
        />
      </>
    );
  },
);

ArtistExport.displayName = 'ArtistExport';
